/**
 * Organization-level feature gates and field config from GET `initial`.
 */
import type { ConfigSocialMedia } from './initialApi';
import { getOrganization } from './initialApi';

export type AddressFieldApiKey =
  | 'address_name'
  | 'phone'
  | 'PACI_number'
  | 'block'
  | 'street'
  | 'house_name'
  | 'avenue'
  | 'floor'
  | 'flat'
  | 'landmark'
  | 'zip_code'
  | 'notes';

export type AddressFormFieldKey =
  | 'name'
  | 'phone'
  | 'PACI_number'
  | 'block'
  | 'street'
  | 'house_name'
  | 'avenue'
  | 'floor'
  | 'flat'
  | 'landmark'
  | 'zip_code'
  | 'driver_note';

const DEFAULT_ADDRESS_FIELD_KEYS: AddressFieldApiKey[] = [
  'address_name',
  'phone',
  'PACI_number',
  'block',
  'street',
  'house_name',
  'avenue',
  'floor',
  'flat',
  'landmark',
  'zip_code',
  'notes',
];

const ADDRESS_API_TO_FORM: Record<AddressFieldApiKey, AddressFormFieldKey> = {
  address_name: 'name',
  phone: 'phone',
  PACI_number: 'PACI_number',
  block: 'block',
  street: 'street',
  house_name: 'house_name',
  avenue: 'avenue',
  floor: 'floor',
  flat: 'flat',
  landmark: 'landmark',
  zip_code: 'zip_code',
  notes: 'driver_note',
};

const REQUIRED_ADDRESS_FORM_FIELDS = new Set<AddressFormFieldKey>([
  'name',
  'block',
  'street',
  'house_name',
  'phone',
]);

export type OrganizationSocialLink = {
  key: string;
  label: string;
  url: string;
  icon: 'whatsapp' | 'instagram' | 'youtube';
};

function parseOrgFlag(value: number | undefined | null, default_value: boolean): boolean {
  if (value == null) {
    return default_value;
  }
  return Boolean(Number(value));
}

export function isLoyaltyFeatureEnabled(): boolean {
  return parseOrgFlag(getOrganization()?.is_loyalty_enabled, false);
}

export function getConfiguredAddressFieldKeys(): AddressFieldApiKey[] {
  const raw = getOrganization()?.address_fields;
  if (!Array.isArray(raw) || raw.length === 0) {
    return DEFAULT_ADDRESS_FIELD_KEYS;
  }
  const allowed = new Set<string>(DEFAULT_ADDRESS_FIELD_KEYS);
  const filtered = raw
    .map(v => (typeof v === 'string' ? v.trim() : ''))
    .filter((v): v is AddressFieldApiKey => allowed.has(v));
  return filtered.length > 0 ? filtered : DEFAULT_ADDRESS_FIELD_KEYS;
}

export function getVisibleAddressFormFields(): Set<AddressFormFieldKey> {
  const keys = getConfiguredAddressFieldKeys().map(api_key => ADDRESS_API_TO_FORM[api_key]);
  return new Set(keys);
}

export function isAddressFormFieldVisible(field: AddressFormFieldKey): boolean {
  return getVisibleAddressFormFields().has(field);
}

export function isAddressFormFieldRequired(field: AddressFormFieldKey): boolean {
  return REQUIRED_ADDRESS_FORM_FIELDS.has(field) && isAddressFormFieldVisible(field);
}

function normalizeExternalUrl(raw: string | undefined): string | null {
  if (raw == null || typeof raw !== 'string') {
    return null;
  }
  const trimmed = raw.trim();
  if (!trimmed) {
    return null;
  }
  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  return `https://${trimmed}`;
}

function buildWhatsappUrl(raw: string | undefined, dialing_code: string | undefined): string | null {
  if (raw == null || typeof raw !== 'string') {
    return null;
  }
  const digits = raw.replace(/\D/g, '');
  if (!digits) {
    return null;
  }
  const dc = String(dialing_code ?? '965').replace(/\D/g, '');
  const normalized = digits.startsWith(dc) ? digits : `${dc}${digits}`;
  return `https://wa.me/${normalized}`;
}

export function resolveOrganizationSocialLinks(): OrganizationSocialLink[] {
  const org = getOrganization();
  const social: ConfigSocialMedia | undefined = org?.configurations?.social_media;
  const general = org?.configurations?.general;
  const links: OrganizationSocialLink[] = [];

  const whatsapp_url = buildWhatsappUrl(general?.whatsapp_number ?? org?.phone, org?.dialing_code);
  if (whatsapp_url) {
    links.push({ key: 'whatsapp', label: 'WhatsApp', url: whatsapp_url, icon: 'whatsapp' });
  }

  const instagram_url = normalizeExternalUrl(social?.instagram_URL);
  if (instagram_url) {
    links.push({ key: 'instagram', label: 'Instagram', url: instagram_url, icon: 'instagram' });
  }

  const youtube_url =
    normalizeExternalUrl(social?.LinkedIn_URL) ?? normalizeExternalUrl((social as { youtube_URL?: string })?.youtube_URL);
  if (youtube_url) {
    links.push({ key: 'youtube', label: 'YouTube', url: youtube_url, icon: 'youtube' });
  }

  return links;
}

/** Checkout place-order flow from `organization.payment_mode` (GET initial). */
export type OrganizationPaymentMode = 'online' | 'manual' | 'both';

/**
 * Normalize `organization.payment_mode`.
 * - `online` → pay now (no chooser)
 * - `manual` → request subscription (no chooser)
 * - `both` / unknown / missing → chooser when gateways exist
 */
export function resolveOrganizationPaymentMode(raw?: string | null): OrganizationPaymentMode {
  const mode = String(raw ?? '')
    .trim()
    .toLowerCase();
  if (mode === 'online') {
    return 'online';
  }
  if (mode === 'manual') {
    return 'manual';
  }
  return 'both';
}

